import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, describeApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Camera, Receipt, Warning, CheckCircle, Trash, CircleNotch, UploadSimple, X, FilePdf, ArrowRight, LinkSimple, Sparkle } from "@phosphor-icons/react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;

const PAYMENT_SOURCES = [
  { key: "bank", label: "Bank account / debit", hint: "Paid from a business bank account — a bank transaction will be created." },
  { key: "card", label: "Business card", hint: "Paid with the business credit card." },
  { key: "cash", label: "Cash", hint: "Paid with cash on hand." },
  { key: "owner", label: "Owner paid personally", hint: "You paid out of pocket — booked as an owner contribution the business owes you." },
];

export function ReceiptScanner({ businessId, business, isAgriculture = false, autoScan = 0, focusMode = "", focusReceiptId = "" }) {
  const [highlightId, setHighlightId] = useState("");
  const [sourcePickerId, setSourcePickerId] = useState(null);
  const navigate = useNavigate();
  const [expenses, setExpenses] = useState([]);
  const [txns, setTxns] = useState([]);
  const [categories, setCategories] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [cameraOpen, setCameraOpen] = useState(false);
  const [camReady, setCamReady] = useState(false);
  const [camStartError, setCamStartError] = useState(false);
  const [shooting, setShooting] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const token = localStorage.getItem("bk_token");

  const load = () => {
    api.get(`/businesses/${businessId}/expenses`).then((r) => setExpenses(r.data)).catch(() => {});
    api.get(`/businesses/${businessId}/transactions`).then((r) => setTxns(r.data)).catch(() => {});
  };

  useEffect(() => {
    load();
    api.get(`/businesses/${businessId}/categories`).then((r) => setCategories(r.data.expense || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId]);

  useEffect(() => {
    if (!focusReceiptId || !expenses.some((e) => e.id === focusReceiptId)) return;
    setHighlightId(focusReceiptId);
    const bring = () => {
      const el = document.querySelector(`[data-testid="expense-${focusReceiptId}"]`);
      if (el) window.scrollTo({ top: Math.max(0, window.scrollY + el.getBoundingClientRect().top - 96), behavior: "smooth" });
    };
    // Twice: once now, once after the inbox card / thumbnails above have finished loading and shifted the layout.
    const timers = [120, 750].map((ms) => setTimeout(bring, ms));
    return () => timers.forEach(clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusReceiptId, expenses.length]);

  const uploadOne = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    await api.post(`/businesses/${businessId}/receipts`, fd, { headers: { "Content-Type": "multipart/form-data" } });
  };

  const uploadMany = async (files) => {
    const list = Array.from(files);
    if (list.length === 0) return;
    setUploading(true);
    setProgress({ current: 0, total: list.length });
    let ok = 0, fail = 0;
    for (let i = 0; i < list.length; i++) {
      setProgress({ current: i + 1, total: list.length });
      try { await uploadOne(list[i]); ok++; load(); } catch { fail++; }
    }
    setUploading(false);
    setProgress({ current: 0, total: 0 });
    if (ok) toast.success(`${ok} receipt${ok === 1 ? "" : "s"} scanned — review the details below`);
    if (fail) toast.error(`${fail} couldn't be read. Try clearer, well-lit photos.`);
    load();
  };

  const onFiles = async (e) => {
    const files = e.target.files;
    await uploadMany(files);
    if (fileRef.current) fileRef.current.value = "";
  };

  // On mobile, tapping "Use camera" opens a live in-app camera (works on iOS + Android,
  // no "camera or file" chooser). Same live-camera modal is used on desktop.
  const onCameraButton = () => {
    if (uploading) return;
    openCamera();
  };

  const ACCEPT_RE = /\.(jpe?g|png|webp|heic|pdf)$/i;
  const onDrop = async (e) => {
    e.preventDefault();
    setDragOver(false);
    if (uploading) return;
    const dropped = Array.from(e.dataTransfer?.files || []);
    const valid = dropped.filter((f) => ACCEPT_RE.test(f.name) || f.type.startsWith("image/") || f.type === "application/pdf");
    if (dropped.length === 0) return;
    if (valid.length === 0) { toast.error("Drop images (JPG, PNG, WEBP, HEIC) or PDF files."); return; }
    if (valid.length < dropped.length) toast.message(`Skipped ${dropped.length - valid.length} unsupported file${dropped.length - valid.length === 1 ? "" : "s"}.`);
    await uploadMany(valid);
  };

  // ---- Camera capture ----
  const openCamera = async () => {
    setCameraOpen(true);
    setCamReady(false);
    setCamStartError(false);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" }, audio: false });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
      }
      setCamReady(true);
    } catch {
      // iOS often needs a direct tap to start the camera (e.g. when opened via the header Scan
      // button after navigation). Keep the modal open and offer a one-tap "Start camera".
      setCamStartError(true);
    }
  };

  const closeCamera = () => {
    if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
    setCameraOpen(false);
    setCamReady(false);
    setCamStartError(false);
    setShooting(false);
  };

  // Snap the photo and immediately upload → AI categorization runs automatically.
  const capture = async () => {
    const video = videoRef.current;
    if (!video || !camReady) return;
    setShooting(true);
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth || 1280;
    canvas.height = video.videoHeight || 720;
    canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.92));
    if (!blob) { setShooting(false); return; }
    const file = new File([blob], `receipt-${Date.now()}.jpg`, { type: "image/jpeg" });
    closeCamera();
    await uploadMany([file]);
  };

  useEffect(() => () => closeCamera(), []);

  // Global "Scan" button (header) bumps autoScan → open the camera automatically.
  const autoScanRef = useRef(0);
  useEffect(() => {
    if (autoScan && autoScan !== autoScanRef.current) {
      autoScanRef.current = autoScan;
      onCameraButton();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoScan]);

  const patch = async (id, body) => {
    try {
      const { data } = await api.patch(`/expenses/${id}`, body);
      setExpenses((xs) => xs.map((x) => (x.id === id ? data : x)));
      return data;
    } catch (e) { toast.error(describeApiError(e, "Couldn't save that change.")); }
  };

  const setLocation = (exp, value) => {
    setExpenses((xs) => xs.map((x) => (x.id === exp.id ? { ...x, location_tag: value } : x)));
    patch(exp.id, { location_tag: value });
  };

  const setItemCategory = (exp, idx, category) => {
    const items = exp.items.map((it, i) => (i === idx ? { ...it, category } : it));
    patch(exp.id, { items });
  };

  const [recheckingId, setRecheckingId] = useState(null);
  const recheckCategories = async (exp) => {
    setRecheckingId(exp.id);
    try {
      const { data } = await api.post(`/expenses/${exp.id}/recategorize`);
      setExpenses((xs) => xs.map((x) => (x.id === exp.id ? data : x)));
      (data.changes?.length ? toast.success : toast.info)(data.rowan_message || "Categories re-checked.", { duration: 8000 });
    } catch (e) { toast.error(describeApiError(e, "Rowan couldn't re-check those categories.")); }
    finally { setRecheckingId(null); }
  };

  const del = async (exp) => {
    const target = typeof exp === "string" ? expenses.find((x) => x.id === exp) : exp;
    const id = target?.id || exp;
    if (target && isPosted(target) && target.matching_status !== "auto_matched" && target.matching_status !== "manual_matched") {
      if (!window.confirm(`This receipt was posted to your books as an expense (${money(target.total)}). Deleting it also removes that ledger entry. Continue?`)) return;
    }
    try {
      const { data } = await api.delete(`/expenses/${id}`);
      setExpenses((xs) => xs.filter((x) => x.id !== id));
      toast.success(data?.removed_transactions ? `Receipt deleted — ${data.removed_transactions} ledger row${data.removed_transactions === 1 ? "" : "s"} removed with it.` : "Receipt deleted.");
      load();
    } catch (e) { toast.error(describeApiError(e, "Couldn't delete that receipt.")); }
  };

  const pendingCount = expenses.filter((e) => e.status === "pending_review").length;
  const autoMatchedCount = expenses.filter((e) => e.matching_status === "auto_matched").length;
  const suggestedReceipts = expenses.filter((e) => e.matching_status === "suggested");
  const isPosted = (e) => !!e.posted || e.status === "posted" || e.posting_status === "posted_expense" || e.posting_status === "posted_matched";
  const unmatchedReceipts = expenses.filter((e) => e.matching_status === "unmatched" && !isPosted(e));
  const unattachedTxns = useMemo(() => txns.filter((t) => t.type === "expense" && !t.receipt_id), [txns]);
  const confirmed = expenses.filter((e) => e.status === "confirmed");
  const catTotals = {};
  confirmed.forEach((e) => (e.items || []).forEach((it) => {
    catTotals[it.category] = (catTotals[it.category] || 0) + (Number(it.amount) || 0);
  }));
  const summaryRows = Object.entries(catTotals).sort((a, b) => b[1] - a[1]);
  const grandTotal = summaryRows.reduce((a, [, v]) => a + v, 0);
  const maxCat = summaryRows.length ? summaryRows[0][1] : 0;

  const busyLabel = progress.total > 1 ? `Scanning ${progress.current} of ${progress.total}…` : "Reading receipt…";
  const openMatchedTxn = (exp) => navigate(`/business/${businessId}?tab=books&txn=${exp.matched_txn_id}`);
  const confirmSuggestedMatch = async (exp) => {
    const data = await patch(exp.id, { matched_txn_id: exp.matched_txn_id });
    if (data?.rowan_match_message) toast.success(data.rowan_match_message);
  };
  const clearSuggestedMatch = async (exp) => {
    // Keeping it unmatched confirms the document only — nothing posts until a match or a payment source is chosen.
    const data = await patch(exp.id, { clear_match: true, status: "confirmed" });
    if (data) toast.info("Kept unmatched — confirmed receipt, not posted to books. Match it or create the expense when you're ready.");
  };

  const [fixOpenId, setFixOpenId] = useState(null);
  const renderFixPanel = (exp) => (
    <div className="space-y-3" data-testid={`receipt-fix-panel-${exp.id}`}>
      <div className="rounded-lg p-3" style={{ background: "var(--bk-page)" }}>
        <p className="text-xs font-semibold uppercase tracking-[0.12em]" style={{ color: "var(--bk-text-2)" }}>Pick the bank transaction that paid for this</p>
        {loadingCands ? (
          <p className="text-sm mt-2 inline-flex items-center gap-2" style={{ color: "var(--bk-text-2)" }}><CircleNotch size={14} className="animate-spin" /> Looking for close matches…</p>
        ) : candidates.length === 0 ? (
          <p className="text-sm mt-2" style={{ color: "var(--bk-text-2)" }}>No bank transactions close to this amount or date. If it wasn't paid from a connected account, use Create expense and choose the payment source.</p>
        ) : (
          <div className="mt-2 space-y-1.5 max-h-72 overflow-y-auto pr-1">
            {candidates.map((c) => (
              <button key={c.id} onClick={() => matchTo(exp, c.id)} data-testid={`match-candidate-${exp.id}-${c.id}`} className="w-full flex items-center justify-between gap-3 rounded-lg border bg-white px-3 py-2 text-left hover:border-[var(--bk-accent)] transition-colors" style={{ borderColor: "var(--bk-border)" }}>
                <span className="min-w-0">
                  <span className="block text-sm font-medium truncate" style={{ color: "var(--bk-text)" }}>{c.description || "Transaction"}</span>
                  <span className="block text-xs" style={{ color: "var(--bk-text-2)" }}>{c.date}{c.account_name ? ` · ${c.account_name}` : ""}</span>
                </span>
                <span className="text-sm font-semibold shrink-0" style={{ color: c.amount_diff <= 0.5 ? "#2F855A" : "var(--bk-text)" }}>{money(c.amount)}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="flex flex-wrap gap-2 justify-between">
        <div className="flex flex-wrap gap-2">
          <button onClick={() => { setFixOpenId(null); setSourcePickerId(exp.id); }} data-testid={`fix-create-expense-${exp.id}`} className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-bold text-white" style={{ background: "#2F855A" }}>
            <Receipt size={14} weight="bold" /> Not in the bank feed — create expense
          </button>
          <button onClick={() => { setFixOpenId(null); del(exp); }} data-testid={`fix-delete-${exp.id}`} className="inline-flex items-center gap-1.5 rounded-full border px-4 py-2 text-xs font-bold bg-white" style={{ borderColor: "var(--bk-border)", color: "var(--bk-energy)" }}>
            <Trash size={14} weight="bold" /> Not a real receipt — delete
          </button>
        </div>
        <button onClick={() => setFixOpenId(null)} data-testid={`fix-cancel-${exp.id}`} className="inline-flex items-center gap-1.5 rounded-full border px-4 py-2 text-xs font-bold bg-white" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }}>
          <X size={14} weight="bold" /> Cancel
        </button>
      </div>
    </div>
  );
  const [candidates, setCandidates] = useState([]);
  const [loadingCands, setLoadingCands] = useState(false);

  const openFix = async (exp) => {
    setSourcePickerId(null);
    setFixOpenId(exp.id);
    setCandidates([]);
    setLoadingCands(true);
    try {
      const { data } = await api.get(`/expenses/${exp.id}/match-candidates`);
      setCandidates(data.candidates || []);
    } catch { setCandidates([]); } finally { setLoadingCands(false); }
  };

  const matchTo = async (exp, txnId) => {
    const data = await patch(exp.id, { matched_txn_id: txnId });
    if (data) { toast.success(data.rowan_match_message || "Matched — receipt attached to that transaction."); setFixOpenId(null); load(); }
  };

  const createExpense = async (exp, source) => {
    const data = await patch(exp.id, { payment_source: source, create_expense: true });
    if (data) {
      toast.success(`Expense created — ${exp.merchant || "receipt"} ${money(exp.total)} posted (${PAYMENT_SOURCES.find((s) => s.key === source)?.label.toLowerCase()}).`);
      setSourcePickerId(null); setFixOpenId(null); load();
    }
  };

  return (
    <div className="space-y-6" data-testid="receipt-scanner">
      <div className="bk-card p-6 flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
        <div>
          <h3 className="font-display text-xl font-bold" style={{ color: "var(--bk-text)" }}>Snap a receipt</h3>
          <p className="text-sm mt-1" style={{ color: "var(--bk-text-2)" }}>
            Use your camera or drop in several at once — AI reads each one, splits the line items, and suggests categories. You confirm before it's final.
          </p>
        </div>
        <input ref={fileRef} type="file" accept=".jpg,.jpeg,.png,.webp,.heic,.pdf,image/jpeg,image/png,image/webp,image/heic,application/pdf" multiple onChange={onFiles} className="hidden" data-testid="receipt-file-input" />
        <div className="flex flex-wrap gap-2 shrink-0">
          <button
            onClick={onCameraButton}
            disabled={uploading}
            data-testid="camera-open-btn"
            className="bk-btn-primary inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full text-sm font-semibold disabled:opacity-70"
          >
            {uploading ? <><CircleNotch size={18} weight="bold" className="animate-spin" /> {busyLabel}</> : <><Camera size={18} weight="fill" /> Use camera</>}
          </button>
          <button
            onClick={() => navigate(`/receipt-rules?business=${businessId}`)}
            data-testid="receipt-rules-manager-link"
            className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full text-sm font-semibold border"
            style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }}
          >
            <Sparkle size={18} weight="fill" color="var(--bk-gold)" /> Learned rules
          </button>
          <button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            data-testid="scan-receipt-btn"
            className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full text-sm font-semibold border disabled:opacity-70"
            style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }}
          >
            <UploadSimple size={18} weight="bold" /> Upload receipts
          </button>
        </div>
      </div>

      <div
        data-testid="receipt-dropzone"
        onClick={() => !uploading && fileRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); if (!uploading) setDragOver(true); }}
        onDragLeave={(e) => { e.preventDefault(); setDragOver(false); }}
        onDrop={onDrop}
        className="rounded-2xl border-2 border-dashed p-8 text-center cursor-pointer transition-colors"
        style={{
          borderColor: dragOver ? "var(--bk-accent)" : "var(--bk-border)",
          background: dragOver ? "var(--bk-page)" : "transparent",
        }}
      >
        <UploadSimple size={28} weight="duotone" color={dragOver ? "var(--bk-accent)" : "var(--bk-text-2)"} className="mx-auto" />
        <p className="text-sm font-semibold mt-2" style={{ color: dragOver ? "var(--bk-accent)" : "var(--bk-text)" }}>
          {dragOver ? "Drop to scan" : "Drag & drop receipts here"}
        </p>
        <p className="text-xs mt-1" style={{ color: "var(--bk-text-2)" }}>
          or click to browse — JPG, PNG, WEBP, HEIC, or PDF · multiple at once
        </p>
      </div>

      {pendingCount > 0 && (
        <div className="flex items-center gap-2 text-sm font-medium" style={{ color: "var(--bk-energy)" }} data-testid="pending-count">
          <Warning size={18} weight="duotone" /> {pendingCount} expense{pendingCount === 1 ? "" : "s"} waiting for your review
        </div>
      )}

      {(autoMatchedCount > 0 || suggestedReceipts.length > 0 || unmatchedReceipts.length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bk-card p-5" data-testid="receipt-match-auto-card">
            <p className="text-xs uppercase tracking-[0.18em] font-semibold" style={{ color: "var(--bk-text-2)" }}>Matched automatically</p>
            <p className="font-display text-2xl font-bold mt-2" style={{ color: "var(--bk-success)" }}>{autoMatchedCount}</p>
            <p className="text-xs mt-2" style={{ color: "var(--bk-text-2)" }}>{autoMatchedCount ? `Receipt matched. That's one less thing on your to-do list.` : "Nothing auto-matched yet this month."}</p>
          </div>
          <div className="bk-card p-5" data-testid="receipt-match-suggested-card">
            <p className="text-xs uppercase tracking-[0.18em] font-semibold" style={{ color: "var(--bk-text-2)" }}>Suggested matches</p>
            <p className="font-display text-2xl font-bold mt-2" style={{ color: "#B45309" }}>{suggestedReceipts.length}</p>
            <p className="text-xs mt-2" style={{ color: "var(--bk-text-2)" }}>Amount lines up, but Rowan wants your nod before attaching it.</p>
          </div>
          <div className="bk-card p-5" data-testid="receipt-match-needs-attention-card">
            <p className="text-xs uppercase tracking-[0.18em] font-semibold" style={{ color: "var(--bk-text-2)" }}>Need attention</p>
            <p className="font-display text-2xl font-bold mt-2" style={{ color: "#B23B1F" }}>{unmatchedReceipts.length}</p>
            <p className="text-xs mt-2" style={{ color: "var(--bk-text-2)" }}>{unmatchedReceipts.length ? `Heads up — ${unmatchedReceipts.length} receipts still need to be matched. Want to tackle them now?` : "No unmatched receipts waiting on you right now."}</p>
          </div>
        </div>
      )}

      {focusMode === "unattached" && (
        <div className="bk-card p-5 border-2" style={{ borderColor: "#F0C36D", background: "#FFF9ED" }} data-testid="receipt-unattached-card">
          <div className="flex items-center gap-2">
            <Warning size={18} weight="fill" color="#B45309" />
            <h4 className="font-display text-lg font-bold" style={{ color: "#8A5A13" }}>Transactions missing receipts</h4>
          </div>
          <p className="text-sm mt-2" style={{ color: "#7C5A28" }}>
            These expense transactions still need receipt uploads so Rowan can package them cleanly for year-end.
          </p>
          <div className="mt-4 space-y-2">
            {unattachedTxns.length === 0 ? (
              <p className="text-sm font-semibold" style={{ color: "#8A5A13" }}>Nice work — every expense transaction already has a receipt attached.</p>
            ) : unattachedTxns.map((txn) => (
              <div key={txn.id} className="rounded-xl border px-3 py-2 flex items-center justify-between gap-3" style={{ borderColor: "#F0C36D", background: "white" }} data-testid={`receipt-gap-${txn.id}`}>
                <div>
                  <p className="text-sm font-semibold" style={{ color: "var(--bk-text)" }}>{txn.description}</p>
                  <p className="text-xs mt-1" style={{ color: "var(--bk-text-2)" }}>{txn.date} · {txn.category || "Uncategorized"}</p>
                </div>
                <span className="text-sm font-semibold" style={{ color: "var(--bk-text)" }}>{money(Math.abs(txn.amount || 0))}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {summaryRows.length > 0 && (
        <div className="bk-card p-6" data-testid="expense-summary">
          <div className="flex items-baseline justify-between mb-4">
            <h3 className="font-display text-xl font-bold" style={{ color: "var(--bk-text)" }}>Spending by category</h3>
            <span className="font-display text-2xl font-bold" style={{ color: "var(--bk-text)" }} data-testid="summary-total">{money(grandTotal)}</span>
          </div>
          <div className="space-y-3">
            {summaryRows.map(([cat, val]) => (
              <div key={cat} data-testid={`summary-row-${cat}`}>
                <div className="flex justify-between text-sm mb-1">
                  <span style={{ color: "var(--bk-text)" }}>{cat}</span>
                  <span className="font-semibold" style={{ color: "var(--bk-text)" }}>{money(val)}</span>
                </div>
                <div className="h-2.5 rounded-full overflow-hidden" style={{ background: "var(--bk-page)" }}>
                  <div className="h-2.5 rounded-full" style={{ width: `${maxCat ? (val / maxCat) * 100 : 0}%`, background: "var(--bk-accent)" }} />
                </div>
              </div>
            ))}
          </div>
          <p className="text-xs mt-4" style={{ color: "var(--bk-text-2)" }}>Based on {confirmed.length} confirmed receipt{confirmed.length === 1 ? "" : "s"}. Confirm pending receipts to include them.</p>
        </div>
      )}

      {expenses.length === 0 ? (
        <div className="bk-card p-10 text-center">
          <Receipt size={40} weight="duotone" color="var(--bk-accent)" className="mx-auto" />
          <p className="font-semibold mt-3" style={{ color: "var(--bk-text)" }}>No receipts yet</p>
          <p className="text-sm mt-1" style={{ color: "var(--bk-text-2)" }}>Snap one with your camera or upload a batch to see AI categorize them here.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {expenses.map((exp) => {
            const pending = exp.status === "pending_review";
            const posting = exp.posting_status || (pending ? "pending" : isPosted(exp) ? "posted_expense" : "not_posted");
            const isHighlighted = highlightId === exp.id;
            return (
              <div
                key={exp.id}
                data-testid={`expense-${exp.id}`}
                className="rounded-lg border p-5 transition-colors"
                style={{ background: pending ? "var(--bk-energy-bg)" : "#fff", borderColor: isHighlighted ? "var(--bk-success)" : pending ? "var(--bk-accent)" : "var(--bk-border)", boxShadow: isHighlighted ? "0 0 0 3px rgba(47,133,90,0.25)" : "none" }}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3">
                    {exp.receipt_path && (
                      exp.receipt_path.toLowerCase().endsWith(".pdf") ? (
                        <a
                          href={`${BACKEND}/api/receipt-image/${exp.receipt_path}?auth=${token}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`receipt-pdf-${exp.id}`}
                          className="w-12 h-12 rounded border shrink-0 flex flex-col items-center justify-center gap-0.5"
                          style={{ borderColor: "var(--bk-border)", background: "var(--bk-page)", color: "var(--bk-energy)" }}
                          title="Open PDF receipt"
                        >
                          <FilePdf size={22} weight="duotone" />
                          <span className="text-[9px] font-bold leading-none">PDF</span>
                        </a>
                      ) : (
                        <img
                          src={`${BACKEND}/api/receipt-image/${exp.receipt_path}?auth=${token}`}
                          alt="receipt"
                          className="w-12 h-12 rounded object-cover border shrink-0"
                          style={{ borderColor: "var(--bk-border)" }}
                        />
                      )
                    )}
                    <div>
                      <div className="flex items-center gap-2">
                        {pending ? (
                          <span className="inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: "var(--bk-energy-bg)", color: "var(--bk-energy)" }} data-testid={`badge-pending-${exp.id}`}>
                            <Warning size={12} weight="fill" /> Pending review
                          </span>
                        ) : posting === "not_posted" ? (
                          <span className="inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: "#FFF3D6", color: "#8A5A13" }} data-testid={`badge-not-posted-${exp.id}`}>
                            <Warning size={12} weight="fill" /> Confirmed receipt — not posted to books
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: "var(--bk-success-bg)", color: "var(--bk-success)" }} data-testid={`badge-confirmed-${exp.id}`}>
                            <CheckCircle size={12} weight="fill" /> {posting === "posted_matched" ? "Confirmed · matched to bank transaction" : `Confirmed · posted as expense${exp.payment_source_label ? ` (${exp.payment_source_label.toLowerCase()})` : ""}`}
                          </span>
                        )}
                        {highlightId === exp.id && (
                          <span className="inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: "var(--bk-success)", color: "#fff" }} data-testid={`receipt-added-${exp.id}`}>
                            <CheckCircle size={12} weight="fill" /> Receipt added.
                          </span>
                        )}
                      </div>
                      <p className="font-display text-lg font-bold mt-1.5" style={{ color: "var(--bk-text)" }}>{exp.merchant || "Unknown merchant"}</p>
                      <p className="text-xs" style={{ color: "var(--bk-text-2)" }}>{exp.date || "No date"} · Total {money(exp.total)}{exp.tax ? ` · Tax ${money(exp.tax)}` : ""}</p>
                      {exp.matching_status === "auto_matched" || exp.matching_status === "manual_matched" ? (
                        <div className="mt-2 flex flex-wrap items-center gap-2" data-testid={`receipt-match-success-${exp.id}`}>
                          <span className="inline-flex items-center gap-1 text-xs font-bold px-2 py-1 rounded-full" style={{ background: "var(--bk-success-bg)", color: "var(--bk-success)" }}>
                            <CheckCircle size={12} weight="fill" /> Matched to transaction {exp.matched_txn_date} {exp.matched_txn_amount ? money(exp.matched_txn_amount) : ""}
                          </span>
                          <button onClick={() => openMatchedTxn(exp)} data-testid={`receipt-open-match-${exp.id}`} className="text-xs font-semibold inline-flex items-center gap-1" style={{ color: "var(--bk-accent)" }}>
                            <LinkSimple size={12} weight="bold" /> Open transaction
                          </button>
                        </div>
                      ) : exp.matching_status === "suggested" ? (
                        <div className="mt-2 rounded-xl border px-3 py-2" style={{ borderColor: "#FCE7C6", background: "#FFF9ED" }} data-testid={`receipt-match-suggested-${exp.id}`}>
                          <p className="text-xs font-semibold" style={{ color: "#8A5A13" }}>Suggested match: {exp.matched_txn_date} · {exp.matched_txn_amount ? money(exp.matched_txn_amount) : ""} · {exp.matched_txn_description || "Transaction"}</p>
                          <div className="flex flex-wrap gap-2 mt-2">
                            <button onClick={() => confirmSuggestedMatch(exp)} data-testid={`receipt-confirm-match-${exp.id}`} className="bk-btn-primary inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-full">
                              <CheckCircle size={12} weight="fill" /> Confirm match
                            </button>
                            <button onClick={() => clearSuggestedMatch(exp)} data-testid={`receipt-clear-match-${exp.id}`} className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-full border" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }}>
                              <X size={12} weight="bold" /> Keep unmatched
                            </button>
                          </div>
                        </div>
                      ) : (
                        !isPosted(exp) && <p className="text-xs mt-2" style={{ color: "#B23B1F" }} data-testid={`receipt-match-unmatched-${exp.id}`}>{exp.rowan_match_message || "This receipt doesn't have a matching transaction yet. Either it's not in your bank feed or it was paid cash."}</p>
                      )}
                    </div>
                  </div>
                  <button onClick={() => del(exp)} data-testid={`delete-expense-${exp.id}`} className="p-1.5 rounded hover:bg-black/5 shrink-0" style={{ color: "var(--bk-energy)" }} aria-label="Delete"><Trash size={16} weight="bold" /></button>
                </div>

                <div className="mt-4 space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-xs font-semibold uppercase tracking-[0.12em]" style={{ color: "var(--bk-text-2)" }}>Line items &amp; categories</p>
                    <button onClick={() => recheckCategories(exp)} disabled={recheckingId === exp.id} data-testid={`recheck-categories-${exp.id}`} className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border hover:bg-[#F6EEDF] disabled:opacity-60" style={{ borderColor: "var(--bk-border)", color: "var(--bk-accent)" }} title="Rowan re-checks every line against this business's categories">
                      {recheckingId === exp.id ? <CircleNotch size={12} className="animate-spin" /> : <Sparkle size={12} weight="fill" color="var(--bk-gold)" />} {recheckingId === exp.id ? "Re-checking…" : "Re-check categories with Rowan"}
                    </button>
                  </div>
                  {isAgriculture && (
                    <input
                      value={exp.location_tag || ""}
                      onChange={(e) => setLocation(exp, e.target.value)}
                      placeholder="Field / ranch / location"
                      data-testid={`expense-location-${exp.id}`}
                      className="w-full rounded-md border px-3 py-2 text-sm outline-none"
                      style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }}
                      list="ag-expense-locations"
                    />
                  )}
                  {(exp.items || []).map((it, idx) => (
                    <div key={idx} className="flex items-center gap-3 flex-wrap sm:flex-nowrap p-2.5 rounded-md" style={{ background: pending ? "#fff" : "var(--bk-page)" }}>
                      <span className="flex-1 min-w-0 text-sm truncate" style={{ color: "var(--bk-text)" }}>{it.description || "Item"}
                        {it.category_source === "learned" && <span className="ml-2 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full" style={{ background: "var(--bk-rowan-bg)", color: "var(--bk-accent)" }} data-testid={`item-learned-${exp.id}-${idx}`}>Your usual</span>}
                      </span>
                      <select
                        value={it.category}
                        onChange={(e) => setItemCategory(exp, idx, e.target.value)}
                        data-testid={`item-category-${exp.id}-${idx}`}
                        className="text-sm rounded-md border px-2.5 py-1.5 bg-white outline-none"
                        style={{ borderColor: categories.length && !categories.includes(it.category) ? "var(--bk-energy)" : "var(--bk-border)", color: "var(--bk-text)" }}
                      >
                        {categories.length > 0 && !categories.includes(it.category) && <option value={it.category}>{it.category} (not in your list — pick one)</option>}
                        {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                      <span className="text-sm font-semibold w-20 text-right shrink-0" style={{ color: "var(--bk-text)" }}>{money(it.amount)}</span>
                    </div>
                  ))}
                </div>

                {(pending || posting === "not_posted") && (
                  <div className="mt-4 rounded-xl border p-3" style={{ borderColor: "var(--bk-border)", background: "#fff" }} data-testid={`receipt-posting-actions-${exp.id}`}>
                    <p className="text-xs" style={{ color: "var(--bk-text-2)" }}>
                      {pending
                        ? "Confirming records the receipt only. To put it in your books, match it to a bank transaction or create an expense with a payment source."
                        : "This receipt is confirmed but nothing has been posted to your books yet. Match it to the bank transaction that paid for it, or create an expense and say how it was paid."}
                    </p>
                    <div className="mt-2 flex flex-wrap gap-2 justify-end">
                      {pending && (
                        <button onClick={() => patch(exp.id, { status: "confirmed" })} data-testid={`confirm-expense-${exp.id}`} className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold border" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }}>
                          <CheckCircle size={16} weight="bold" /> Confirm receipt
                        </button>
                      )}
                      <button onClick={() => openFix(exp)} data-testid={`receipt-match-btn-${exp.id}`} className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold border" style={{ borderColor: "var(--bk-accent)", color: "var(--bk-accent)" }}>
                        <LinkSimple size={16} weight="bold" /> Match to bank transaction
                      </button>
                      <button onClick={() => { setFixOpenId(null); setSourcePickerId(exp.id); }} data-testid={`receipt-create-expense-btn-${exp.id}`} className="bk-btn-primary inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold">
                        <Receipt size={16} weight="bold" /> Create expense
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {unmatchedReceipts.length > 0 && (
        <div className="bk-card p-5 border-2" style={{ borderColor: "#E7B6A8", background: "#FFF8F5" }} data-testid="receipt-unmatched-section">
          <div className="flex items-center gap-2">
            <Warning size={18} weight="fill" color="#B23B1F" />
            <h4 className="font-display text-lg font-bold" style={{ color: "#8A2E19" }}>Receipts without transactions</h4>
          </div>
          <p className="text-sm mt-2" style={{ color: "#7C2D12" }}>These receipts still need a matching bank transaction, so Rowan parked them here for follow-up.</p>
          <div className="mt-4 space-y-2">
            {unmatchedReceipts.map((exp) => (
              <div key={exp.id} className="rounded-xl border bg-white" style={{ borderColor: "#F3C9BE" }} data-testid={`receipt-unmatched-row-${exp.id}`}>
                <button
                  type="button"
                  onClick={() => openFix(exp)}
                  className="w-full px-3 py-2 flex items-center justify-between gap-3 text-left"
                  data-testid={`receipt-unmatched-fix-${exp.id}`}
                >
                  <div>
                    <p className="text-sm font-semibold" style={{ color: "var(--bk-text)" }}>{exp.merchant || "Unknown merchant"}</p>
                    <p className="text-xs mt-1" style={{ color: "var(--bk-text-2)" }}>{exp.date || "No date"} · {money(exp.total)}</p>
                  </div>
                  <span className="text-xs font-semibold inline-flex items-center gap-1 shrink-0" style={{ color: "#B23B1F" }}>
                    Fix it <ArrowRight size={12} weight="bold" />
                  </span>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {(() => {
        const exp = expenses.find((e) => e.id === sourcePickerId);
        return (
          <Dialog open={!!exp} onOpenChange={(o) => { if (!o) setSourcePickerId(null); }}>
            {exp && (
              <DialogContent className="sm:max-w-lg" data-testid={`receipt-source-picker-${exp.id}`}>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2" style={{ color: "var(--bk-text)" }}>
                    <Receipt size={20} weight="duotone" color="var(--bk-accent)" /> How was this paid?
                  </DialogTitle>
                </DialogHeader>
                <p className="text-sm -mt-1" style={{ color: "var(--bk-text-2)" }} data-testid="receipt-source-summary">
                  <span className="font-semibold" style={{ color: "var(--bk-text)" }}>{exp.merchant || "Unknown merchant"}</span> · {money(exp.total)}{exp.date ? ` · ${exp.date}` : ""}. Pick the source and Rowan posts it as an expense right away.
                </p>
                <div className="space-y-2">
                  {PAYMENT_SOURCES.map((src) => (
                    <button key={src.key} onClick={() => createExpense(exp, src.key)} data-testid={`receipt-source-${src.key}-${exp.id}`} className="w-full text-left rounded-xl border bg-white px-4 py-3 hover:border-[var(--bk-accent)] hover:bg-[#FBF7EF] transition-colors" style={{ borderColor: "var(--bk-border)" }}>
                      <span className="block text-sm font-semibold" style={{ color: "var(--bk-text)" }}>{src.label}</span>
                      <span className="block text-xs mt-0.5" style={{ color: "var(--bk-text-2)" }}>{src.hint}</span>
                    </button>
                  ))}
                </div>
                <div className="flex justify-between items-center gap-2 pt-1">
                  <button onClick={() => { setSourcePickerId(null); openFix(exp); }} data-testid={`receipt-source-switch-match-${exp.id}`} className="text-xs font-semibold inline-flex items-center gap-1" style={{ color: "var(--bk-accent)" }}>
                    <LinkSimple size={12} weight="bold" /> It's already in my bank feed — match instead
                  </button>
                  <button onClick={() => setSourcePickerId(null)} data-testid={`receipt-source-cancel-${exp.id}`} className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-full border" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }}>
                    <X size={15} weight="bold" /> Cancel
                  </button>
                </div>
              </DialogContent>
            )}
          </Dialog>
        );
      })()}

      {(() => {
        const exp = expenses.find((e) => e.id === fixOpenId);
        return (
          <Dialog open={!!exp} onOpenChange={(o) => { if (!o) setFixOpenId(null); }}>
            {exp && (
              <DialogContent className="sm:max-w-xl" data-testid={`receipt-match-dialog-${exp.id}`}>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2" style={{ color: "var(--bk-text)" }}>
                    <LinkSimple size={20} weight="duotone" color="var(--bk-accent)" /> Match to a bank transaction
                  </DialogTitle>
                </DialogHeader>
                <p className="text-sm -mt-1" style={{ color: "var(--bk-text-2)" }} data-testid="receipt-match-summary">
                  <span className="font-semibold" style={{ color: "var(--bk-text)" }}>{exp.merchant || "Unknown merchant"}</span> · {money(exp.total)}{exp.date ? ` · ${exp.date}` : ""}. Matching attaches the receipt to the transaction that already paid for it — nothing is double-counted.
                </p>
                {renderFixPanel(exp)}
              </DialogContent>
            )}
          </Dialog>
        );
      })()}

      <Dialog open={cameraOpen} onOpenChange={(o) => { if (!o) closeCamera(); }}>
        <DialogContent className="sm:max-w-lg" data-testid="camera-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ color: "var(--bk-text)" }}>
              <Camera size={20} weight="duotone" color="var(--bk-accent)" /> Snap a receipt
            </DialogTitle>
          </DialogHeader>
          <div className="rounded-lg overflow-hidden bg-black relative" style={{ aspectRatio: "4 / 3" }}>
            <video ref={videoRef} playsInline muted autoPlay className="w-full h-full object-cover" data-testid="camera-video" />
            {!camReady && !camStartError && (
              <div className="absolute inset-0 flex items-center justify-center text-white text-sm gap-2">
                <CircleNotch size={18} className="animate-spin" /> Starting camera…
              </div>
            )}
            {camStartError && (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-white text-sm gap-3 px-6 text-center">
                <Camera size={28} weight="duotone" />
                <p>Tap to turn on your camera. Allow camera access if your phone asks.</p>
                <button onClick={openCamera} data-testid="camera-start-btn" className="bk-btn-primary inline-flex items-center gap-2 text-sm font-semibold px-5 py-2 rounded-full">
                  <Camera size={16} weight="fill" /> Start camera
                </button>
              </div>
            )}
          </div>
          <p className="text-xs" style={{ color: "var(--bk-text-2)" }}>
            Line the receipt up in good light, then capture — we'll read and categorize it automatically.
          </p>
          <div className="flex justify-between gap-2">
            <button onClick={closeCamera} data-testid="camera-close-btn" className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-full border" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }}>
              <X size={15} weight="bold" /> Cancel
            </button>
            <button onClick={capture} disabled={!camReady || shooting} data-testid="camera-capture-btn" className="bk-btn-primary inline-flex items-center gap-2 text-sm font-semibold px-5 py-2 rounded-full disabled:opacity-60">
              {shooting ? <><CircleNotch size={16} className="animate-spin" /> Scanning…</> : <><Camera size={16} weight="fill" /> Capture &amp; scan</>}
            </button>
          </div>
        </DialogContent>
      </Dialog>
      {isAgriculture && (
        <datalist id="ag-expense-locations">
          {(business?.agriculture_settings?.locations || []).map((loc) => <option key={loc} value={loc} />)}
        </datalist>
      )}
    </div>
  );
}
